import { Button, Spacer } from '@sparemin/blockhead';
import React, { useCallback, useState } from 'react';

import AutomationPostModalBackButton from 'components/AutomationPostModalBackButton';
import DynamicElementsPopper from 'components/DynamicElementsPopper';
import EducationalCard from 'components/EducationalCard/EducationalCard';
import ModalContentWithMedia from 'components/Modal/ModalContentWithMedia';
import { Step } from 'components/SteppedModal';
import { block, videoDescriptionMetadata } from '../../utils';
import YoutubeVideoDescriptionInput from '../../YoutubeVideoDescriptionInput';

interface UseVideoDescriptionStepProps {
  description: string;
  isActive: boolean;
  readonly?: boolean;
  hideProgress: boolean;
  onDescriptionChange: (description: string) => void;
  onBackClick: () => void;
  onHideModal: () => void;
}

export default function useVideoDescriptionStep({
  description,
  isActive,
  readonly,
  hideProgress,
  onDescriptionChange,
  onBackClick,
  onHideModal,
}: UseVideoDescriptionStepProps): Step {
  const [defaultDescription, setDefaultDescription] = useState(description);

  const handleBackClick = useCallback((): void => {
    onDescriptionChange(defaultDescription);
    onBackClick();
  }, [defaultDescription, onBackClick, onDescriptionChange]);

  if (!isActive && description !== defaultDescription) {
    setDefaultDescription(description);
  }

  return {
    component: (
      <>
        <ModalContentWithMedia
          onClose={onHideModal}
          className="composer-step-content"
        >
          <ModalContentWithMedia.ModalContentWithMediaColumn>
            <img
              src="https://static.sparemin.com/static/webapp-assets/auto-posting-detail/auto-posting-detail-image-c-202303301450.png"
              className={block('composer-image-left')}
            />
          </ModalContentWithMedia.ModalContentWithMediaColumn>

          <ModalContentWithMedia.ModalContentWithMediaColumn className="composer-content-column">
            <Spacer
              orientation="vertical"
              align="center"
              justify="center"
              space={2.5}
              className={block('step-container')}
            >
              <AutomationPostModalBackButton
                text="Video descriptions"
                description="Format how descriptions post with each video. You can add up to 5000 characters."
                onClick={handleBackClick}
              />

              <YoutubeVideoDescriptionInput
                onChange={onDescriptionChange}
                readonly={readonly}
                value={description}
                placeholder="Add description"
                hideProgress={hideProgress}
                showLabel={false}
              />

              <EducationalCard>
                <DynamicElementsPopper
                  buttonContent="add content from your rss feed"
                  elementsList={videoDescriptionMetadata}
                  onClick={metaValue =>
                    onDescriptionChange(`${description} ${metaValue}`)
                  }
                />
              </EducationalCard>

              <Button
                onClick={onBackClick}
                variant="solid"
                className={block('composer-step-submit-button')}
              >
                save changes
              </Button>
            </Spacer>
          </ModalContentWithMedia.ModalContentWithMediaColumn>
        </ModalContentWithMedia>
      </>
    ),
    id: 'video-description-step',
    hideFooter: true,
    replaceBody: true,
  };
}
